/**
 * Data transformation utilities for API communication
 * Handles conversion between form state (strings) and database state (null for optional empty values)
 */

/**
 * Sanitizes optional profile fields by converting empty strings to null
 * Required fields should already be validated by Zod before this runs
 *
 * @param data - The profile data to sanitize
 * @returns Sanitized data with empty optional fields converted to null
 */
export function sanitizeProfileForDatabase(data: any): any {
  const sanitized = { ...data };

  // Only sanitize OPTIONAL fields - required fields are validated by Zod
  const optionalFields = ['street_address_line_2', 'avatar_url', 'subgroup_id'];

  for (const field of optionalFields) {
    if (field in sanitized) {
      if (sanitized[field] === '' || sanitized[field] === undefined) {
        sanitized[field] = null;
      } else if (typeof sanitized[field] === 'string') {
        const trimmed = sanitized[field].trim();
        sanitized[field] = trimmed === '' ? null : trimmed;
      }
    }
  }

  return sanitized;
}

/**
 * Converts null values to empty strings for form display
 *
 * @param data - The profile data from database
 * @returns Data with null values converted to empty strings for form inputs
 */
export function sanitizeProfileForForm(data: any): any {
  const sanitized = { ...data };

  for (const [key, value] of Object.entries(sanitized)) {
    if (value === null || value === undefined) {
      sanitized[key] = '';
    }
  }

  return sanitized;
}
